import { type Product } from '@wix/ambassador-forms-v4-form/types';
import React, {
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  RadioGroup as RadioGroupRAC,
  Radio as RadioRAC,
  TextField as TextFieldRAC,
  Input as InputRAC,
} from 'react-aria-components';
import { debounce } from 'ts-debounce';

import { useConfig } from '../../contexts/config-context';
import { useFocusFieldEvent } from '../../contexts/events-context';
import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import {
  useTranslation,
  type TFunction,
} from '../../contexts/translation-context';
import { PRODUCT_PRICE_MULTIPLE_OF } from '../../field-settings/settings-shared/products/product-price';
import { findChildOfType, applyInternalProps } from '../../services/find-child';
import { type Currency } from '../../types';
import { formatCurrency } from '../../utils/format-currency';
import { mapById } from '../../utils/map-by-field';
import { Description } from '../field-description/field-description-headless';
import { Error } from '../field-error/field-error-headless';
import { Label } from '../field-label/field-label-headless';
import { getValidNumberInput } from '../shared/numeric-input-utils';
import { getCurrencyAriaLabel } from '../shared/payment-field-utils';

import { type DonationFieldProps } from './donation-field-props';
import {
  findProductWithDynamicPrice,
  getCustomAmount,
  getProductValueOnCheckboxChange,
  toProductValue,
} from './donation-field-utils';

export type DonationProps = {
  children: ReactNode;
};

export const Donation = ({ children }: DonationProps) => {
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    onBlur,
    onFocus,
    validation,
    addOther,
    addOtherPlaceholder,
    FieldLayout,
    hasError,
  } = useFieldPropsV2<DonationFieldProps>();

  const { t } = useTranslation();
  const { currency, regionalFormat } = useConfig();

  const productValue = value?.[0];
  const products = useMemo(
    () => validation?.predefined?.paymentOptions?.products || [],
    [validation],
  );
  const productsById = useMemo(() => mapById(products), [products]);
  const customAmountProduct = useMemo(
    () => (addOther ? findProductWithDynamicPrice(products) : undefined),
    [products, addOther],
  );

  const [customAmount, setCustomAmount] = useState(
    getCustomAmount(productValue, customAmountProduct),
  );

  const handleRadioChange = useCallback(
    (selectedValue: string) => {
      onChange(
        getProductValueOnCheckboxChange(
          selectedValue,
          productValue,
          productsById,
        ),
      );
      setCustomAmount('');
    },
    [onChange, productsById, productValue],
  );

  const handleCustomAmountChange = useCallback(
    (amount: number | undefined) => {
      if (!customAmountProduct) {
        return;
      }
      if (typeof amount === 'number') {
        onChange([toProductValue(customAmountProduct, String(amount))]);
      } else {
        onChange([]);
      }
    },
    [onChange, customAmountProduct],
  );

  const debouncedHandleCustomAmountChange = useMemo(() => {
    return debounce(handleCustomAmountChange, 500);
  }, [handleCustomAmountChange]);

  const handleCustomAmountInputChange: React.ChangeEventHandler<HTMLInputElement> =
    useCallback(
      (event) => {
        if (productValue?.productId !== customAmountProduct?.id) {
          onChange(undefined);
        }
        const { validInput, validNumber } = getValidNumberInput(
          event.target.value,
          PRODUCT_PRICE_MULTIPLE_OF,
          customAmount,
          regionalFormat,
        );
        setCustomAmount(validInput);
        debouncedHandleCustomAmountChange(validNumber);
      },
      [
        regionalFormat,
        onChange,
        productValue,
        customAmountProduct,
        customAmount,
        debouncedHandleCustomAmountChange,
      ],
    );

  const selectedValue = productValue?.productId || null;

  const handleFocusCustomInput = useCallback(() => {
    if (customAmountProduct?.id && selectedValue !== customAmountProduct.id) {
      handleRadioChange(customAmountProduct.id);
    }
  }, [customAmountProduct?.id, selectedValue, handleRadioChange]);

  const labelElement = findChildOfType(children, Donation.Label);
  const controlElement = findChildOfType(children, Donation.Control);
  const descriptionElement = findChildOfType(children, Donation.Description);
  const errorElement = findChildOfType(children, Donation.Error);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() => labelElement}
      renderInput={() => (
        <RadioGroupRAC
          value={selectedValue}
          onChange={handleRadioChange}
          isDisabled={disabled}
          isInvalid={hasError}
          isRequired={required}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          {controlElement
            ? applyInternalProps<ControlInternalProps>(controlElement, {
                productsById,
                addOther,
                customAmount,
                onCustomAmountInputChange: handleCustomAmountInputChange,
                currency,
                t,
                placeholder:
                  addOtherPlaceholder === undefined
                    ? t('donation.other-option.placeholder')
                    : addOtherPlaceholder,
                customAmountProductId: customAmountProduct?.id,
                onFocusCustomInput: handleFocusCustomInput,
              })
            : null}
        </RadioGroupRAC>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type ControlProps = {
  children: ReactNode;
  className?: string;
};

type ControlInternalProps = {
  productsById: Record<string, Product>;
  addOther?: boolean;
  customAmount: string;
  onCustomAmountInputChange: React.ChangeEventHandler<HTMLInputElement>;
  currency: Currency | undefined;
  t: TFunction;
  placeholder: string;
  customAmountProductId?: string;
  onFocusCustomInput: () => void;
};

const Control = ({ children, className, ...internal }: ControlProps) => {
  const {
    productsById,
    addOther,
    customAmount,
    onCustomAmountInputChange,
    currency,
    t,
    placeholder,
    customAmountProductId,
    onFocusCustomInput,
  } = internal as ControlInternalProps;
  const { hasError } = useFieldPropsV2<DonationFieldProps>();

  const optionsElement = findChildOfType(children, Donation.Options);
  const otherInputElement = findChildOfType(children, Donation.OtherInput);

  return (
    <div className={className} data-invalid={hasError}>
      {optionsElement
        ? applyInternalProps<OptionsInternalProps>(optionsElement, {
            productsById,
            addOther,
            customAmountProductId,
            t,
          })
        : null}
      {otherInputElement && addOther
        ? applyInternalProps<OtherInputInternalProps>(otherInputElement, {
            customAmount,
            onCustomAmountInputChange,
            currency,
            t,
            placeholder,
            customAmountProductId,
            onFocusCustomInput,
          })
        : null}
    </div>
  );
};

type OptionsProps = {
  children: ReactNode;
  className?: string;
};

type OptionsInternalProps = {
  productsById: Record<string, Product>;
  addOther?: boolean;
  customAmountProductId?: string;
  t: TFunction;
};

const Options = ({ children, className, ...internal }: OptionsProps) => {
  const { productsById, addOther, customAmountProductId, t } =
    internal as OptionsInternalProps;
  const { options } = useFieldPropsV2<DonationFieldProps>();

  const optionElement = findChildOfType(children, Options.Option);

  return (
    <div className={className}>
      {options.map((option) => {
        const product = productsById[option.value];
        const price = product?.fixedPriceOptions?.price;

        return applyInternalProps<OptionInternalProps>(optionElement, {
          key: option.value,
          value: option.value,
          price: price || '0',
        });
      })}
      {addOther && customAmountProductId && (
        <RadioRAC
          value={customAmountProductId}
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: '0',
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: '0',
          }}
          aria-label={t('donation.other-option.label', 'Custom amount')}
        >
          {t('donation.other-option.label', 'Custom amount')}
        </RadioRAC>
      )}
    </div>
  );
};

type OptionProps = {
  children: ReactNode;
  className?: string;
};

type OptionInternalProps = {
  value: string;
  price: string;
};

const Option = ({ children, className, ...internal }: OptionProps) => {
  const { value, price } = internal as OptionInternalProps;
  const { id } = useFieldPropsV2<DonationFieldProps>();

  const valueElement = findChildOfType(children, Option.Value);
  const currencyElement = findChildOfType(children, Option.Currency);

  const ref = useRef<HTMLLabelElement>(null);
  useFocusFieldEvent(() => {
    ref.current?.querySelector('input')?.focus();
  }, id);

  return (
    <RadioRAC value={value} className={className} ref={ref}>
      <>
        {valueElement
          ? applyInternalProps<OptionValueInternalProps>(valueElement, {
              price,
            })
          : null}
        {currencyElement}
      </>
    </RadioRAC>
  );
};

type OptionValueProps = {
  className?: string;
};

type OptionValueInternalProps = {
  price: string;
};

const OptionValue = ({ className, ...internal }: OptionValueProps) => {
  const { price } = internal as OptionValueInternalProps;
  const { regionalFormat } = useConfig();

  const formattedPrice = formatCurrency({
    amount: price,
    locale: regionalFormat,
  });

  return <span className={className}>{formattedPrice}</span>;
};

type OptionCurrencyProps = {
  className?: string;
};

const OptionCurrency = ({ className }: OptionCurrencyProps) => {
  const { currency } = useConfig();

  return (
    <span className={className}>{currency?.sign || currency?.code || ''}</span>
  );
};

Option.Value = OptionValue;
Option.Currency = OptionCurrency;

type OtherInputProps = {
  children: ReactNode;
  className?: string;
};

type OtherInputInternalProps = {
  customAmount: string;
  onCustomAmountInputChange: React.ChangeEventHandler<HTMLInputElement>;
  currency: Currency | undefined;
  t: TFunction;
  placeholder: string;
  customAmountProductId?: string;
  onFocusCustomInput: () => void;
};

const OtherInput = ({ children, className, ...internal }: OtherInputProps) => {
  const {
    customAmount,
    onCustomAmountInputChange,
    currency,
    t,
    placeholder,
    customAmountProductId,
    onFocusCustomInput,
  } = internal as OtherInputInternalProps;
  const { disabled, hasError } = useFieldPropsV2<DonationFieldProps>();

  const currencyElement = findChildOfType(children, OtherInput.Currency);
  const valueElement = findChildOfType(children, OtherInput.Value);

  const handleChange = useCallback(
    (value: string) => {
      const event = {
        target: { value },
      } as React.ChangeEvent<HTMLInputElement>;
      onCustomAmountInputChange(event);
    },
    [onCustomAmountInputChange],
  );

  const handleFocus = useCallback(() => {
    onFocusCustomInput();
  }, [onFocusCustomInput]);

  const hasValue = customAmount && customAmount.trim() !== '';

  return (
    <TextFieldRAC
      value={customAmount}
      onChange={handleChange}
      onFocus={handleFocus}
      isDisabled={disabled}
      isInvalid={hasError}
      className={className}
      data-selected={hasValue ? 'true' : undefined}
    >
      {customAmount && currency && currencyElement
        ? applyInternalProps<OtherInputCurrencyInternalProps>(currencyElement, {
            currency,
            t,
          })
        : null}
      {valueElement
        ? applyInternalProps<OtherInputValueInternalProps>(valueElement, {
            placeholder,
            customAmountProductId,
          })
        : null}
    </TextFieldRAC>
  );
};

type OtherInputCurrencyProps = {
  className?: string;
};

type OtherInputCurrencyInternalProps = {
  currency: Currency | undefined;
  t: TFunction;
};

const OtherInputCurrency = ({
  className,
  ...internal
}: OtherInputCurrencyProps) => {
  const { currency, t } = internal as OtherInputCurrencyInternalProps;
  const { prefixId } = useFieldAttributes();

  return (
    <span
      id={prefixId}
      aria-hidden={false}
      aria-label={getCurrencyAriaLabel(t, currency)}
      className={className}
    >
      {currency?.sign || currency?.code || ''}
    </span>
  );
};

type OtherInputValueProps = {
  className?: string;
};

type OtherInputValueInternalProps = {
  placeholder: string;
  customAmountProductId?: string;
};

const OtherInputValue = ({ className, ...internal }: OtherInputValueProps) => {
  const { placeholder, customAmountProductId } =
    internal as OtherInputValueInternalProps;
  const { ariaDescribedBy, labelId, prefixId } = useFieldAttributes();

  return (
    <InputRAC
      id={customAmountProductId}
      aria-labelledby={`${labelId} ${prefixId}`}
      aria-describedby={ariaDescribedBy}
      placeholder={placeholder}
      inputMode="decimal"
      autoComplete="off"
      className={className}
    />
  );
};

OtherInput.Currency = OtherInputCurrency;
OtherInput.Value = OtherInputValue;

Options.Option = Option;

Donation.Label = Label;
Donation.Control = Control;
Donation.Options = Options;
Donation.OtherInput = OtherInput;
Donation.Description = Description;
Donation.Error = Error;
